
import { useEffect, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { Trophy, Users, TrendingUp, Medal, Crown, Target } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";

/**
 * Interface for friend leaderboard data
 */
interface FriendStats {
  user_id: string;
  username: string;
  points: number;
  rank: number;
  avatar_url?: string;
}

/**
 * Interface for competitive analytics
 */
interface CompetitiveAnalytics {
  userRankAmongFriends: number;
  totalFriends: number;
  pointsToNextFriend: number;
  topFriendPoints: number;
  averageFriendPoints: number;
  userPercentile: number;
}

/**
 * CompetitiveStats Component
 * 
 * Displays competitive statistics comparing the user's performance
 * with their friends, including rankings, points comparisons, and achievements.
 */
const CompetitiveStats = () => {
  const { toast } = useToast();
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  // Get current user ID on component mount
  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setCurrentUserId(user.id);
        console.log('🔍 Current user ID set:', user.id);
      }
    };
    getCurrentUser();
  }, []);

  // Fetch friends leaderboard data
  const { data: friendsData, isLoading } = useQuery({
    queryKey: ['friendsCompetitiveStats', currentUserId],
    queryFn: async () => {
      if (!currentUserId) {
        console.log('❌ No current user ID available');
        return null;
      }

      console.log('📊 Fetching competitive stats for user:', currentUserId);

      // Get friends leaderboard data (friends + current user)
      const { data: friendsLeaderboard, error } = await supabase
        .from('leaderboard_view')
        .select('*')
        .or(`is_friend.eq.true,user_id.eq.${currentUserId}`)
        .order('points', { ascending: false });

      if (error) {
        console.error('Error fetching friends leaderboard:', error);
        throw error;
      }

      console.log('👥 Friends leaderboard data:', friendsLeaderboard);
      return friendsLeaderboard || [];
    },
    enabled: !!currentUserId // Only run query when we have a user ID
  });

  // Calculate competitive analytics
  const competitiveAnalytics: CompetitiveAnalytics | null = (() => {
    if (!friendsData || friendsData.length === 0 || !currentUserId) return null;

    // Find current user in the data
    const currentUser = friendsData.find(f => f.user_id === currentUserId);
    if (!currentUser) {
      console.log('❌ Current user not found in friends data');
      return null;
    }

    // Filter out current user to get only friends
    const friendsOnly = friendsData.filter(f => f.user_id !== currentUserId && f.is_friend);
    
    if (friendsOnly.length === 0) {
      console.log('ℹ️ No friends found for competitive analysis');
      return null;
    }

    // Calculate user's rank among friends (including themselves)
    const userRankAmongFriends = friendsData.findIndex(f => f.user_id === currentUserId) + 1;
    
    // Find next friend to beat (friend with more points)
    const friendsAbove = friendsOnly.filter(f => f.points > currentUser.points);
    const nextFriendToBeat = friendsAbove.length > 0 
      ? Math.min(...friendsAbove.map(f => f.points))
      : friendsOnly[0]?.points || 0;
    
    const pointsToNextFriend = Math.max(0, nextFriendToBeat - currentUser.points);
    
    // Calculate statistics
    const totalFriends = friendsOnly.length;
    const topFriendPoints = Math.max(...friendsOnly.map(f => f.points));
    const averageFriendPoints = Math.round(
      friendsOnly.reduce((sum, f) => sum + f.points, 0) / totalFriends
    );
    
    // Calculate percentile (what percentage of friends user is beating)
    const friendsBehind = friendsOnly.filter(f => f.points < currentUser.points).length;
    const userPercentile = totalFriends > 0 ? Math.round((friendsBehind / totalFriends) * 100) : 0;

    console.log('📈 Calculated competitive analytics:', {
      userRankAmongFriends,
      totalFriends,
      pointsToNextFriend,
      topFriendPoints,
      averageFriendPoints,
      userPercentile
    });

    return {
      userRankAmongFriends,
      totalFriends,
      pointsToNextFriend,
      topFriendPoints,
      averageFriendPoints,
      userPercentile
    };
  })();

  // Get top 3 friends for display
  const topFriends = friendsData?.filter(f => f.is_friend).slice(0, 3) || [];

  if (isLoading) {
    return (
      <Card className="bg-background/80 backdrop-blur border-gray-200/50 shadow-sm">
        <CardHeader className="p-6 border-b border-gray-100">
          <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Trophy className="h-5 w-5" />
            Friends Competition
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!competitiveAnalytics || competitiveAnalytics.totalFriends === 0) {
    return (
      <Card className="bg-background/80 backdrop-blur border-gray-200/50 shadow-sm">
        <CardHeader className="p-6 border-b border-gray-100">
          <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Trophy className="h-5 w-5" />
            Friends Competition
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="text-center text-gray-500">
            <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p className="text-lg font-medium mb-1">No friends yet!</p>
            <p className="text-sm">Add friends to see competitive stats and climb the leaderboard together.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overall Competitive Performance */}
      <Card className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
        <CardHeader className="p-6">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Trophy className="h-5 w-5" />
            Your Competitive Standing
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 pt-0">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold">#{competitiveAnalytics.userRankAmongFriends}</div>
              <div className="text-sm text-blue-100">Rank Among Friends</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">{competitiveAnalytics.userPercentile}%</div>
              <div className="text-sm text-blue-100">Friends Beaten</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">{competitiveAnalytics.totalFriends}</div>
              <div className="text-sm text-blue-100">Total Friends</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">{competitiveAnalytics.pointsToNextFriend}</div>
              <div className="text-sm text-blue-100">Points to Next</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Friends Performance Comparison */}
      <Card className="bg-background/80 backdrop-blur border-gray-200/50 shadow-sm">
        <CardHeader className="p-6 border-b border-gray-100">
          <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Performance vs Friends
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-4">
            {/* Progress to beat top friend */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Progress to Top Friend</span>
                <span className="font-medium">{competitiveAnalytics.topFriendPoints} points</span>
              </div>
              <Progress 
                value={competitiveAnalytics.topFriendPoints > 0 ? 
                  Math.min(100, ((competitiveAnalytics.topFriendPoints - competitiveAnalytics.pointsToNextFriend) / competitiveAnalytics.topFriendPoints) * 100) : 0} 
                className="h-2" 
              />
            </div>
            
            {/* Average comparison */}
            <div className="grid grid-cols-2 gap-4 pt-4 border-t">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{competitiveAnalytics.averageFriendPoints}</div>
                <div className="text-sm text-gray-500">Average Friend Points</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{competitiveAnalytics.topFriendPoints}</div>
                <div className="text-sm text-gray-500">Top Friend Points</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Top Friends Leaderboard */}
      {topFriends.length > 0 && (
        <Card className="bg-background/80 backdrop-blur border-gray-200/50 shadow-sm">
          <CardHeader className="p-6 border-b border-gray-100">
            <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Medal className="h-5 w-5" />
              Top Friends
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-3">
              {topFriends.map((friend, index) => {
                const isTopPerformer = index === 0;
                const rankIcon = index === 0 ? Crown : index === 1 ? Medal : Trophy;
                const RankIcon = rankIcon;
                
                return (
                  <div 
                    key={friend.user_id}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      isTopPerformer ? 'bg-yellow-50 border border-yellow-200' : 'bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        isTopPerformer ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-200 text-gray-600'
                      }`}>
                        <RankIcon className="h-4 w-4" />
                      </div>
                      <div>
                        <div className="font-medium">{friend.username}</div>
                        <div className="text-sm text-gray-500">Rank #{friend.rank}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-gray-900">{friend.points}</div>
                      <div className="text-xs text-gray-500">points</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Motivation Card */}
      {competitiveAnalytics.pointsToNextFriend > 0 && (
        <Card className="bg-gradient-to-r from-green-600 to-emerald-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <h3 className="text-lg font-semibold">Keep pushing! 💪</h3>
                <p className="text-green-100">
                  You need {competitiveAnalytics.pointsToNextFriend} more points to beat your next friend!
                </p>
              </div>
              <div className="p-3 bg-background/20 rounded-full">
                <Target className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CompetitiveStats;
